import Image from 'next/image';
import { EventCard, LoadingSpinner } from '@goodneighbor/ui';
import styles from './eventList.module.scss';
import {
  causeTypeMap,
  getEventCardDate,
  parseForEventCardProps
} from '@/services/event';
import { useEvents } from '../../hooks/useEvents';
import { cx } from 'class-variance-authority';
import useFeaturedLocations from '@/hooks/api/events/useFeaturedLocations';
import useFlagsQuery from '@/hooks/api/flags/useFlagsQuery';

type UseEventsReturn = ReturnType<typeof useEvents>;

export type EventListProps = {
  events: UseEventsReturn['events'];
  viewMode: UseEventsReturn['viewMode'];
  centerPoint: UseEventsReturn['centerPoint'];
  resetToLocation: (
    lat: number,
    lng: number,
    bounds?: { north: number; south: number; east: number; west: number }
  ) => void;
  onSelectEvent: (id: string) => void;
  searchQuery?: string;
  className?: string;
};

export default function EventList({
  events,
  viewMode,
  centerPoint,
  resetToLocation,
  onSelectEvent,
  searchQuery,
  className
}: EventListProps) {
  const { featuredLocations } = useFeaturedLocations();
  const { data: flags } = useFlagsQuery();
  const hasKindnessChallenges =
    flags?.data['kindness-challenge_enabled'] ?? false;

  const listClassName = cx(
    viewMode === 'grid' ? styles.list : styles.listList,
    className
  );

  if (events.length === 0) {
    if (!centerPoint) {
      return <LoadingSpinner />;
    }

    return (
      <div className={styles.emptyStateContainer}>
        <div className={styles.emptyStateContent}>
          <div className={styles.emptyStateInner}>
            <div className={styles.emptyStateImageWrapper}>
              <Image
                src="/placeholder/map-search_empty-state-image.svg"
                alt=""
                fill
                className={styles.emptyStateImage}
                aria-hidden="true"
              />
            </div>
            <h2 className={styles.emptyTitle}>No events nearby</h2>
            <p className={styles.emptyDescription}>
              Search another location or checkout the Kindness Challenge
              everyone is talking about.
            </p>
          </div>
        </div>

        {hasKindnessChallenges && (
          <div className={styles.diySection}>
            <h3 className={styles.exploreTitle}>Explore DIY opportunities</h3>
            <a
              href="https://www.goodneighbordayamerica.com/kindness-challenge"
              className={styles.challengeCard}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className={styles.challengeCardContent}>
                <p className={styles.challengeCardTitle}>
                  Complete a Kindness Challenge Today and you could win
                  exclusive prizes.
                </p>
                <span className={styles.challengeCardLink}>
                  View challenges &rsaquo;
                </span>
              </div>
              <div className={styles.challengeCardImage} aria-hidden>
                <Image
                  src="/placeholder/explore-kindness.svg"
                  alt=""
                  fill
                  style={{ objectFit: 'contain', objectPosition: 'center' }}
                />
              </div>
            </a>
          </div>
        )}

        <div className={styles.exploreSection}>
          <h3 className={styles.exploreTitle}>Explore other locations</h3>
          <div className={styles.cityCardsGrid}>
            {featuredLocations.map((location) => (
              <button
                key={location.key}
                className={styles.cityCard}
                onClick={() =>
                  resetToLocation(location.lat, location.lng, location.bounds)
                }
                type="button"
              >
                <div className={styles.cityCardImageWrapper}>
                  <Image
                    src={location.image}
                    alt={location.shortName}
                    fill
                    style={{ objectFit: 'cover' }}
                  />
                  <div className={styles.cityCardOverlay}>
                    <span className={styles.cityCardName}>
                      {location.shortName}
                    </span>
                    <span className={styles.cityCardCount}>
                      {location.eventCount} events
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={listClassName}>
        {events.map((event) => (
          <EventCard
            key={event.id}
            {...parseForEventCardProps(
              event,
              event.clientName || event.locationName || '',
              {
                hideEndTime: true
              }
            )}
            variant={viewMode === 'list' ? 'compact' : 'vertical'}
            category={
              (event.causeType &&
                (causeTypeMap[event.causeType] || event.causeType)) ||
              undefined
            }
            date={getEventCardDate(event)}
            media={
              event.promoImageUrl ? (
                <Image
                  src={event.promoImageUrl}
                  alt={event.title}
                  fill
                  style={{ objectFit: 'cover' }}
                />
              ) : undefined
            }
            onClick={() => {
              onSelectEvent(event.id);
            }}
          />
        ))}
      </div>
      {hasKindnessChallenges && (
        <div className={styles.diySection}>
          <h3 className={styles.exploreTitle}>Explore DIY opportunities</h3>
          <a
            href="https://www.goodneighbordayamerica.com/kindness-challenge"
            className={styles.challengeCard}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className={styles.challengeCardContent}>
              <p className={styles.challengeCardTitle}>
                Complete a Kindness Challenge Today and you could win exclusive
                prizes.
              </p>
              <span className={styles.challengeCardLink}>
                View challenges &rsaquo;
              </span>
            </div>
            <div className={styles.challengeCardImage} aria-hidden>
              <Image
                src="/placeholder/explore-kindness.svg"
                alt=""
                fill
                style={{ objectFit: 'contain', objectPosition: 'center' }}
              />
            </div>
          </a>
        </div>
      )}
    </>
  );
}
